"use client"

import { useEffect, useRef, useState } from "react"
import Link from "next/link"
import { ChevronDown, Menu, X } from "lucide-react"
import { AREAS_EN_ORDEN, MODULOS, NOMBRE_DE_AREA } from "@/lib/landing/catalogo"
import { PAGINAS_DE_SECTOR } from "@/lib/landing/sectores"
import { areaDe } from "@/lib/landing/precio"
import { geist } from "./fuente"

type Desplegable = "modulos" | "sectores" | "recursos"

/** Lo más buscado de cada sector: atajos a las páginas módulo × sector. */
const MAS_BUSCADO = [
  { href: "/para/hosteleria/fichaje", texto: "Control horario en hostelería" },
  { href: "/para/hosteleria/produccion", texto: "Escandallos de restaurante" },
  { href: "/para/hosteleria/turnos", texto: "Cuadrantes para restaurantes" },
  { href: "/para/ecommerce/inventario", texto: "Inventario para Shopify" },
  { href: "/para/servicios/proyectos", texto: "Proyectos para agencias" },
  { href: "/para/software/teletrabajo", texto: "Registro del teletrabajo" },
]

const RECURSOS = [
  { href: "/carta-digital", texto: "Carta digital gratis", detalle: "Tu carta con QR y tu ficha en la guía" },
  { href: "/restaurantes", texto: "Guía de restaurantes", detalle: "Bares y restaurantes por isla y municipio" },
  { href: "/plantillas", texto: "Plantillas", detalle: "Para gestionar tu negocio, gratis" },
  { href: "/calculadora", texto: "Calculadoras", detalle: "Food cost, márgenes, plantilla…" },
  { href: "/glosario", texto: "Glosario", detalle: "Los términos, con fórmulas y ejemplos" },
  { href: "/mejores", texto: "Comparativas", detalle: "Las herramientas, comparadas" },
]

const DIRECTOS = [
  { href: "/work", texto: "Zindra Work" },
  { href: "/precios", texto: "Precios" },
]

/**
 * La cabecera de la web pública. Plana, sobre el fondo de la página, con el
 * logotipo de siempre y una sola llamada: montar tu Zindra.
 *
 * **Desplegables donde hay más de un sitio al que ir**: Módulos (los 21, por
 * área), Sectores (los seis y lo más buscado) y Recursos. Zindra Work y Precios
 * son una página cada uno, así que van directos. Se abren al pasar por encima
 * o al pulsar, se cierran con Escape o pulsando fuera; en el móvil, acordeón.
 *
 * `fija`: para las páginas de antes (directorio, glosario, plantillas…), que
 * dejan hueco arriba para una cabecera fija.
 */
export function Cabecera({ activo, fija = false }: { activo?: string; fija?: boolean }) {
  const [movil, setMovil] = useState(false)
  const [abierto, setAbierto] = useState<Desplegable | null>(null)
  const [enMovil, setEnMovil] = useState<Desplegable | null>(null)
  const cierre = useRef<ReturnType<typeof setTimeout> | null>(null)
  const raiz = useRef<HTMLElement>(null)

  useEffect(() => {
    if (!abierto) return
    const fuera = (e: MouseEvent) => {
      if (raiz.current && !raiz.current.contains(e.target as Node)) setAbierto(null)
    }
    const tecla = (e: KeyboardEvent) => {
      if (e.key === "Escape") setAbierto(null)
    }
    document.addEventListener("mousedown", fuera)
    document.addEventListener("keydown", tecla)
    return () => {
      document.removeEventListener("mousedown", fuera)
      document.removeEventListener("keydown", tecla)
    }
  }, [abierto])

  const entrar = (d: Desplegable) => {
    if (cierre.current) clearTimeout(cierre.current)
    setAbierto(d)
  }
  const salir = () => {
    cierre.current = setTimeout(() => setAbierto(null), 160)
  }
  const cerrarTodo = () => {
    setAbierto(null)
    setMovil(false)
    setEnMovil(null)
  }

  const boton = (d: Desplegable, texto: string, activoSi: string) => (
    <button
      type="button"
      aria-expanded={abierto === d}
      aria-controls={`menu-${d}`}
      onClick={() => setAbierto((a) => (a === d ? null : d))}
      onMouseEnter={() => entrar(d)}
      onMouseLeave={salir}
      className={`flex items-center gap-1 py-2 text-[15px] text-[#0b0b0c] hover:text-lime-700 ${activo === activoSi ? "font-semibold" : ""}`}
    >
      {texto}
      <ChevronDown className={`h-4 w-4 transition-transform ${abierto === d ? "rotate-180" : ""}`} aria-hidden />
    </button>
  )

  return (
    <header
      ref={raiz}
      className={`${geist.className} border-b border-zinc-200 ${
        fija ? "fixed inset-x-0 top-0 z-50 bg-[#f4f4f5]/95 backdrop-blur" : "relative z-40 bg-[#f4f4f5]"
      }`}
    >
      <nav aria-label="Principal" className="mx-auto flex max-w-[1240px] flex-wrap items-center gap-x-7 gap-y-3 px-6 py-3">
        <Link href="/" className="flex items-center" aria-label="Zindra, inicio" onClick={cerrarTodo}>
          <img src="/zindra_black.svg" alt="Zindra" className="block h-[26px] w-auto" />
        </Link>

        <div className="hidden flex-1 items-center gap-x-6 lg:flex">
          {boton("modulos", "Módulos", "Módulos")}
          {boton("sectores", "Sectores", "Sectores")}
          {DIRECTOS.map((e) => (
            <Link
              key={e.href}
              href={e.href}
              className={`py-2 text-[15px] text-[#0b0b0c] hover:text-lime-700 ${activo === e.texto ? "font-semibold" : ""}`}
            >
              {e.texto}
            </Link>
          ))}
          {boton("recursos", "Recursos", "Recursos")}
        </div>

        <div className="ml-auto flex items-center gap-2">
          <Link href="/login" className="hidden px-3.5 py-2.5 text-[15px] text-[#0b0b0c] hover:text-lime-700 sm:block">
            Entrar
          </Link>
          <Link href="/precios" className="rounded-xl bg-[#0b0b0c] px-4 py-2.5 text-[15px] font-medium text-white hover:bg-zinc-800">
            Montar mi Zindra
          </Link>
          <button
            type="button"
            className="flex h-11 w-11 items-center justify-center rounded-xl lg:hidden"
            onClick={() => setMovil((a) => !a)}
            aria-label={movil ? "Cerrar menú" : "Abrir menú"}
            aria-expanded={movil}
          >
            {movil ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      {/* ── Desplegables (escritorio) ─────────────────────────────────── */}
      {abierto && (
        <div
          id={`menu-${abierto}`}
          className="absolute inset-x-0 top-full hidden border-b border-zinc-200 bg-white lg:block"
          onMouseEnter={() => entrar(abierto)}
          onMouseLeave={salir}
        >
          <div className="mx-auto max-w-[1240px] px-6 py-7">
            {abierto === "modulos" && (
              <div className="flex flex-col gap-5">
                <div className="grid grid-cols-4 gap-8">
                  {AREAS_EN_ORDEN.map((area) => (
                    <div key={area} className="flex flex-col gap-1">
                      <Link
                        href={`/modulos#${area}`}
                        onClick={cerrarTodo}
                        className="mb-1.5 flex items-baseline justify-between gap-2 border-b border-zinc-100 pb-2"
                      >
                        <span className="text-[13px] font-semibold uppercase tracking-wide text-[#0b0b0c]">{NOMBRE_DE_AREA[area]}</span>
                        <span className="font-mono text-[11px] text-lime-700">{area === "equipo" ? "0,50 € / persona" : "3 € / mes"}</span>
                      </Link>
                      {MODULOS.filter((m) => areaDe(m.clave) === area).map((m) => (
                        <Link
                          key={m.slug}
                          href={`/modulos/${m.slug}`}
                          onClick={cerrarTodo}
                          title={m.resumen}
                          className="rounded-lg px-2 py-1.5 text-[15px] text-zinc-700 hover:bg-zinc-100 hover:text-[#0b0b0c]"
                        >
                          {m.nombre}
                        </Link>
                      ))}
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-[#f4f4f5] px-4 py-3">
                  <span className="text-sm text-zinc-600">Cada módulo funciona solo y comparte datos con los demás. Núcleo de 5 € al mes.</span>
                  <div className="flex gap-4 text-sm font-medium">
                    <Link href="/modulos" onClick={cerrarTodo} className="hover:text-lime-700">
                      Ver los 21 módulos →
                    </Link>
                    <Link href="/precios" onClick={cerrarTodo} className="hover:text-lime-700">
                      Calcular el precio →
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {abierto === "sectores" && (
              <div className="grid grid-cols-[2fr_1fr] gap-10">
                <div className="grid grid-cols-2 gap-2">
                  {PAGINAS_DE_SECTOR.map((p) => (
                    <Link
                      key={p.slug}
                      href={`/para/${p.slug}`}
                      onClick={cerrarTodo}
                      className="flex flex-col gap-0.5 rounded-xl px-3 py-2.5 hover:bg-zinc-100"
                    >
                      <span className="text-[15px] font-semibold text-[#0b0b0c]">{p.nombre}</span>
                      <span className="text-[13px] text-zinc-500">{p.frase}</span>
                    </Link>
                  ))}
                </div>
                <div className="flex flex-col gap-1 border-l border-zinc-100 pl-8">
                  <span className="mb-1.5 font-mono text-[11px] tracking-[0.06em] text-zinc-500">LO MÁS BUSCADO</span>
                  {MAS_BUSCADO.map((e) => (
                    <Link key={e.href} href={e.href} onClick={cerrarTodo} className="rounded-lg px-2 py-1.5 text-[14px] text-zinc-700 hover:bg-zinc-100 hover:text-[#0b0b0c]">
                      {e.texto}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {abierto === "recursos" && (
              <div className="grid grid-cols-3 gap-2">
                {RECURSOS.map((r) => (
                  <Link key={r.href} href={r.href} onClick={cerrarTodo} className="flex flex-col gap-0.5 rounded-xl px-3 py-2.5 hover:bg-zinc-100">
                    <span className="text-[15px] font-semibold text-[#0b0b0c]">{r.texto}</span>
                    <span className="text-[13px] text-zinc-500">{r.detalle}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Menú del móvil: acordeón ──────────────────────────────────── */}
      {movil && (
        <div className="max-h-[calc(100dvh-64px)] overflow-y-auto border-t border-zinc-200 px-6 pb-6 lg:hidden">
          {(
            [
              { d: "modulos" as const, texto: "Módulos" },
              { d: "sectores" as const, texto: "Sectores" },
            ]
          ).map(({ d, texto }) => (
            <div key={d} className="border-b border-zinc-200">
              <button
                type="button"
                aria-expanded={enMovil === d}
                onClick={() => setEnMovil((a) => (a === d ? null : d))}
                className="flex w-full items-center justify-between py-3.5 text-base text-[#0b0b0c]"
              >
                {texto}
                <ChevronDown className={`h-4 w-4 transition-transform ${enMovil === d ? "rotate-180" : ""}`} aria-hidden />
              </button>
              {enMovil === d && d === "modulos" && (
                <div className="flex flex-col gap-3 pb-4">
                  {AREAS_EN_ORDEN.map((area) => (
                    <div key={area} className="flex flex-col">
                      <span className="pb-1 text-[12px] font-semibold uppercase tracking-wide text-zinc-500">{NOMBRE_DE_AREA[area]}</span>
                      <div className="flex flex-wrap gap-1.5">
                        {MODULOS.filter((m) => areaDe(m.clave) === area).map((m) => (
                          <Link
                            key={m.slug}
                            href={`/modulos/${m.slug}`}
                            onClick={cerrarTodo}
                            className="rounded-lg border border-zinc-200 bg-white px-2.5 py-1.5 text-sm"
                          >
                            {m.nombre}
                          </Link>
                        ))}
                      </div>
                    </div>
                  ))}
                  <Link href="/modulos" onClick={cerrarTodo} className="text-sm font-medium">
                    Ver los 21 módulos →
                  </Link>
                </div>
              )}
              {enMovil === d && d === "sectores" && (
                <div className="flex flex-col pb-3">
                  {PAGINAS_DE_SECTOR.map((p) => (
                    <Link key={p.slug} href={`/para/${p.slug}`} onClick={cerrarTodo} className="flex flex-col py-2">
                      <span className="text-[15px] font-medium">{p.nombre}</span>
                      <span className="text-[13px] text-zinc-500">{p.frase}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
          {DIRECTOS.map((e) => (
            <Link key={e.href} href={e.href} onClick={cerrarTodo} className="block border-b border-zinc-200 py-3.5 text-base">
              {e.texto}
            </Link>
          ))}
          <div className="border-b border-zinc-200">
            <button
              type="button"
              aria-expanded={enMovil === "recursos"}
              onClick={() => setEnMovil((a) => (a === "recursos" ? null : "recursos"))}
              className="flex w-full items-center justify-between py-3.5 text-base text-[#0b0b0c]"
            >
              Recursos
              <ChevronDown className={`h-4 w-4 transition-transform ${enMovil === "recursos" ? "rotate-180" : ""}`} aria-hidden />
            </button>
            {enMovil === "recursos" && (
              <div className="flex flex-col pb-3">
                {RECURSOS.map((r) => (
                  <Link key={r.href} href={r.href} onClick={cerrarTodo} className="py-2 text-[15px]">
                    {r.texto}
                  </Link>
                ))}
              </div>
            )}
          </div>
          <Link href="/login" onClick={cerrarTodo} className="block py-3.5 text-base">
            Entrar
          </Link>
        </div>
      )}
    </header>
  )
}
